import { useQuery } from 'react-query';

import awsConfig from '../aws-exports';
import { getJwtToken } from '../components/common/Auth/JwtHelper';

const PROXY_URL_PATH = 'licenseAcquisition';
export const ARCUS_DRM_PROVIDER = 'Arcus';

const fetchArcusLicenseUrl = async (input: ArcusLaUrlInput): Promise<string> => {
	if (input.drmProvider.toLocaleLowerCase() !== ARCUS_DRM_PROVIDER.toLocaleLowerCase()) {
		throw new Error(`Invalid DRM provider: ${input.drmProvider.toLocaleLowerCase()}. Expected: ${ARCUS_DRM_PROVIDER.toLocaleLowerCase()}`);
	}

	const proxyUrl = new URL(`${awsConfig.arcusLicenseAcquisitionEndpointUrl}/${PROXY_URL_PATH}`);
	
	for (const [key, value] of Object.entries(input)) {
		proxyUrl.searchParams.append(key, value);
	}

	const jwtToken = await getJwtToken();

	const response = await fetch(proxyUrl.toString(), {
		method: 'GET',
		mode: 'cors',
		headers: {
			Authorization: `Bearer ${jwtToken}`,
			'Content-Type': 'text/plain'
		}
	});

	if (!response.ok) {
		const errorText = await response.text();
		throw new Error(`Failed to fetch Arcus license URL: ${errorText}`);
	}

	return response.text();
};

/**
 * Custom hook to fetch and manage the Arcus License Agreement (LA) URL.
 *
 * @param {ArcusLaUrlInput | undefined} input - The input parameters required to fetch the LA URL.
 * @returns {ArcusLaUrl} An object containing the fetched LA URL and the loading state.
 *
 * @typedef {Object} ArcusLaUrlInput
 * @property {string} configAlias - The configuration alias.
 * @property {string} contentRef - The content reference.
 * @property {string} propositionContentId - The proposition content ID.
 * @property {string} protection - The protection parameter.
 *
 * @typedef {Object} ArcusLaUrl
 * @property {string} laUrl - The fetched LA URL.
 * @property {boolean} loading - The loading state indicating whether the URL is being fetched.
 *
 * @example
 * const { laUrl, loading } = useArcusLaUrl({
 *   configAlias: 'exampleAlias',
 *   contentRef: 'exampleContentRef',
 *   propositionContentId: 'examplePropositionId',
 *   protection: 'exampleProtection'
 * });
 */
const useLicenseAcquisitionUrl = (input: ArcusLaUrlInput | undefined): ArcusLaUrl => {
	const { data: laUrl, isLoading } = useQuery<string>(
		['arcusLicense', input],
		() => fetchArcusLicenseUrl(input!),
		{
			enabled: !!input && input.drmProvider.toLocaleLowerCase() === ARCUS_DRM_PROVIDER.toLocaleLowerCase(),
		}
	);

	return {
		laUrl: laUrl || '',
		loading: isLoading
	};
};

export type ArcusLaUrlInput = {
	configAlias: string;
	contentRef: string;
	propositionContentId: string;
	protection: string;
	drmProvider: string;
};

export type ArcusLaUrl = {
	laUrl: string;
	loading: boolean;
};

export default useLicenseAcquisitionUrl;
